/**
 * IFRAP-AI.fr - Page Listes de Distribution
 * Gestion des destinataires email avec niveaux de permissions
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Users, UserPlus, Upload, Download, Trash2, Edit, Check, X, RefreshCw, Crown, Shield, User } from 'lucide-react';

const DistributionPage = () => {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Forms state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [newRecipient, setNewRecipient] = useState({
    email: '',
    name: '',
    organization: '',
    level: 'standard',
  });
  const [importText, setImportText] = useState('');
  const [importLevel, setImportLevel] = useState('standard');

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/distribution/lists');
      setLists(res.data.lists || []);
      setStats(res.data.stats);
      
      // Select default list
      if (res.data.lists?.length > 0) {
        const defaultList = res.data.lists.find(l => l.is_default) || res.data.lists[0];
        fetchListDetails(defaultList.id);
      }
    } catch (err) {
      console.error('Error fetching lists:', err);
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const fetchListDetails = async (listId) => {
    try {
      const res = await api.get(`/api/distribution/lists/${listId}`);
      setSelectedList(res.data);
      setRecipients(res.data.recipients || []);
    } catch (err) {
      console.error('Error fetching list details:', err);
    }
  };

  const handleAddRecipient = async (e) => {
    e.preventDefault();
    if (!selectedList) return;

    try {
      await api.post(`/api/distribution/lists/${selectedList.id}/recipients`, {
        ...newRecipient,
        categories: ['veille_think_tank', 'daily_digest'],
      });
      
      setMessage({ type: 'success', text: `${newRecipient.email} ajouté` });
      setNewRecipient({ email: '', name: '', organization: '', level: 'standard' });
      setShowAddForm(false);
      fetchListDetails(selectedList.id);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'ajout';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleRemoveRecipient = async (recipientId) => {
    if (!selectedList || !window.confirm('Supprimer ce destinataire ?')) return;

    try {
      await api.delete(`/api/distribution/lists/${selectedList.id}/recipients/${recipientId}`);
      setMessage({ type: 'success', text: 'Destinataire supprimé' });
      fetchListDetails(selectedList.id);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!selectedList || !importText.trim()) return;

    try {
      const res = await api.post('/api/distribution/import/text', {
        list_id: selectedList.id,
        emails_text: importText,
        default_level: importLevel,
        default_categories: ['veille_think_tank', 'daily_digest'],
      });
      
      setMessage({ 
        type: 'success', 
        text: `Import: ${res.data.imported} ajoutés, ${res.data.duplicates} doublons, ${res.data.invalid} invalides` 
      });
      setImportText('');
      setShowImportForm(false);
      fetchListDetails(selectedList.id);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'import' });
    }
  };

  const handleExport = async () => {
    if (!selectedList) return;
    
    try {
      const res = await api.get(`/api/distribution/lists/${selectedList.id}/export`, {
        responseType: 'blob'
      });
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `distribution_${selectedList.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'export' });
    }
  };

  const handleToggleActive = async (recipientId, isActive) => {
    if (!selectedList) return;

    try {
      await api.put(`/api/distribution/lists/${selectedList.id}/recipients/${recipientId}`, {
        is_active: !isActive,
      });
      fetchListDetails(selectedList.id);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur' });
    }
  };

  const handleChangeLevel = async (recipientId, newLevel) => {
    if (!selectedList) return;

    try {
      await api.put(`/api/distribution/lists/${selectedList.id}/recipients/${recipientId}`, {
        level: newLevel,
      });
      fetchListDetails(selectedList.id);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur' });
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'super': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'standard': return <Shield className="w-4 h-4 text-blue-400" />;
      case 'limited': return <User className="w-4 h-4 text-gray-400" />;
      default: return null;
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'super': return 'Super (reçoit tout)';
      case 'standard': return 'Standard';
      case 'limited': return 'Limité';
      default: return level;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">Listes de Distribution</h1>
          <p className="text-gray-400 text-sm mt-1">Gestion des destinataires email</p>
        </div>
        {stats && (
          <div className="flex items-center gap-4 text-sm">
            <div className="bg-[#1a1a2e] px-3 py-1 rounded">
              <span className="text-gray-400">Total:</span>
              <span className="text-white ml-2">{stats.total_recipients}</span>
            </div>
            <div className="bg-yellow-500/20 px-3 py-1 rounded flex items-center gap-1">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="text-yellow-400">{stats.super_recipients}</span>
            </div>
          </div>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Liste sélecteur */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Listes</h3>
          <div className="space-y-2">
            {lists.map(list => (
              <button
                key={list.id}
                onClick={() => fetchListDetails(list.id)}
                className={`w-full text-left px-3 py-2 rounded transition-colors ${
                  selectedList?.id === list.id 
                    ? 'bg-[#00FF88]/20 border border-[#00FF88]/30' 
                    : 'hover:bg-gray-800'
                }`}
              >
                <p className="text-sm text-white">{list.name}</p>
                <p className="text-xs text-gray-500">
                  {list.active_count} actifs · {list.super_count} super
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Contenu principal */}
        <div className="lg:col-span-3 space-y-4">
          {selectedList && (
            <>
              {/* Actions */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-white">{selectedList.name}</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-[#00FF88] text-black rounded hover:bg-[#00FF88]/90 transition-colors text-sm"
                  >
                    <UserPlus className="w-4 h-4" />
                    Ajouter
                  </button>
                  <button
                    onClick={() => setShowImportForm(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Import
                  </button>
                  <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>

              {/* Add Form */}
              {showAddForm && (
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-[#00FF88] mb-3">Ajouter un destinataire</h3>
                  <form onSubmit={handleAddRecipient} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="email"
                      value={newRecipient.email}
                      onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                      placeholder="Email *"
                      className="bg-black/30 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                      required
                    />
                    <input
                      type="text"
                      value={newRecipient.name}
                      onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                      placeholder="Nom (optionnel)"
                      className="bg-black/30 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                    />
                    <select
                      value={newRecipient.level}
                      onChange={(e) => setNewRecipient({ ...newRecipient, level: e.target.value })}
                      className="bg-black/30 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                    >
                      <option value="super">Super (tout)</option>
                      <option value="standard">Standard</option>
                      <option value="limited">Limité</option>
                    </select>
                    <div className="flex gap-2">
                      <button type="submit" className="flex-1 bg-[#00FF88] text-black rounded py-2 text-sm">
                        Ajouter
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setShowAddForm(false)}
                        className="px-3 bg-gray-700 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Import Form */}
              {showImportForm && (
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-400 mb-3">Import en masse</h3>
                  <form onSubmit={handleImport} className="space-y-3">
                    <textarea
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      placeholder="Collez les emails ici (séparés par virgules, espaces ou retours à la ligne)&#10;&#10;Exemple:&#10;email1@test.com, email2@test.com&#10;email3@test.com"
                      className="w-full h-32 bg-black/30 border border-gray-700 rounded px-3 py-2 text-white text-sm font-mono"
                      required
                    />
                    <div className="flex items-center gap-3">
                      <select
                        value={importLevel}
                        onChange={(e) => setImportLevel(e.target.value)}
                        className="bg-black/30 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                      >
                        <option value="super">Niveau: Super</option>
                        <option value="standard">Niveau: Standard</option>
                        <option value="limited">Niveau: Limité</option>
                      </select>
                      <button type="submit" className="flex-1 bg-blue-600 text-white rounded py-2 text-sm">
                        Importer
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setShowImportForm(false)}
                        className="px-3 py-2 bg-gray-700 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Recipients Table */}
              <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr className="text-left text-xs text-gray-400">
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Nom</th>
                      <th className="px-4 py-3">Niveau</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.length > 0 ? (
                      recipients.map(recipient => (
                        <tr key={recipient.id} className="border-t border-gray-800 hover:bg-black/20">
                          <td className="px-4 py-3 text-sm text-white">{recipient.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">{recipient.name || '-'}</td>
                          <td className="px-4 py-3">
                            <select
                              value={recipient.level}
                              onChange={(e) => handleChangeLevel(recipient.id, e.target.value)}
                              className="bg-black/30 border border-gray-700 rounded px-2 py-1 text-xs text-white"
                            >
                              <option value="super">Super</option>
                              <option value="standard">Standard</option>
                              <option value="limited">Limité</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleToggleActive(recipient.id, recipient.is_active)}
                              className={`px-2 py-1 rounded text-xs ${
                                recipient.is_active 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {recipient.is_active ? 'Actif' : 'Inactif'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleRemoveRecipient(recipient.id)}
                              className="text-gray-400 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          Aucun destinataire dans cette liste
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Légende */}
              <div className="flex items-center gap-6 text-xs text-gray-400">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  <span>Super: Reçoit TOUTES les alertes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <span>Standard: Catégories sélectionnées</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>Limité: Alertes urgentes uniquement</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DistributionPage;
